import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import MicroModal from 'micromodal'; // keep if you still use the video modal
import 'lazysizes';
import "./App.scss";
import { Perks } from "./Perks";

/**
 * Shield Select – App (Refactored, no client-side logging)
 *
 * - Server now logs login during fetch_customer (GET)
 * - Server now logs audits during submit_selection (POST)
 * - This client only calls those two routes.
 */

// =============================
// Config & Utilities
// =============================

const CONFIG = {
  CUSTOMER_ENDPOINT: process.env.REACT_APP_CUSTOMER_ENDPOINT,
  CUSTOMER_TOKEN: process.env.REACT_APP_CUSTOMER_TOKEN,
  COOKIE_MAX_AGE_SECONDS: 60 * 60, // 1 hour
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const jsonPlainHeaders = { "Content-Type": "text/plain;charset=utf-8" };

function stripHtml(input = "") {
  const div = document.createElement("div");
  div.innerHTML = input;
  return div.textContent || div.innerText || "";
}

function setCookie(name, value, maxAgeSeconds) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}`;
}

// =============================
// API Layer
// =============================

async function getCustomerAndAvailablePerks(email) {
  const url = `${CONFIG.CUSTOMER_ENDPOINT}?action=fetch_customer&email=${encodeURIComponent(
    email
  )}&token=${CONFIG.CUSTOMER_TOKEN}`;

  const res = await fetch(url);
  const data = await res.json();

  if (data?.error) {
    throw new Error(data.error);
  }

  return {
    customer: data.customer,
    availablePerks: data.availablePerks || [],
  };
}

async function submitCustomerForm(form) {
  const payload = { ...form, token: CONFIG.CUSTOMER_TOKEN, action: "submit_selection" };

  const res = await fetch(CONFIG.CUSTOMER_ENDPOINT, {
    method: "POST",
    headers: jsonPlainHeaders,
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!data?.success) {
    throw new Error(data?.error || "submit_selection failed");
  }
}

// =============================
// App Component
// =============================

function App() {
  const videoRef = useRef(null);

  const isPostLaunch = false;

  // Auth & user state
  const [isSignIn, setIsSignIn] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [customerData, setCustomerData] = useState({});

  // UI state
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorText, setErrorText] = useState("");

  // Perks state
  const [perksMenu, setPerksMenu] = useState([]);
  const [newSelectedPerks, setNewSelectedPerks] = useState([]); // ["Perk", "Perk - Option"]
  const [giftSelected, setGiftSelected] = useState("No");
  const [customExperience, setCustomExperience] = useState("");
  const [holidayHelper, setHolidayHelper] = useState("");

  // Shields tracking – re-computed from customer + selections
  const [totalShields, setTotalShields] = useState(null);
  const [shieldsUsed, setShieldsUsed] = useState(0);

  // Initialize totals when customer loads
  useEffect(() => {
    if (customerData?.Shields != null && customerData?.Shields_Used != null) {
      const t =
        (parseInt(customerData.Shields, 10) || 0) -
        (parseInt(customerData.Shields_Used, 10) || 0);
      setTotalShields(Math.max(t, 0));
      setShieldsUsed(parseInt(customerData.Shields_Used, 10) || 0);
    }
  }, [customerData]);

  // Sticky header behavior
  useEffect(() => {
    const onScroll = () => {
      const headerHeight = document.querySelector(".header")?.offsetHeight || 0;
      const introHeight = document.querySelector(".intro")?.offsetHeight || 0;
      document.body.classList.toggle(
        "sticky",
        window.scrollY > headerHeight + introHeight
      );
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // =============================
  // Handlers
  // =============================

  const handleLogIn = useCallback(
    async (e) => {
      e.preventDefault();
      setErrorText("");

      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setErrorText("Please enter a valid email address.");
        return;
      }

      setLoginDisabled(true);
      try {
        const result = await getCustomerAndAvailablePerks(trimmed);
        const { customer, availablePerks } = result;

        setCustomerData(customer || {});
        setPerksMenu(Array.isArray(availablePerks) ? availablePerks : []);
        setIsLoggedIn(true);

        setCookie("fdEmail", trimmed.toLowerCase(), CONFIG.COOKIE_MAX_AGE_SECONDS);

        if (!trimmed.toLowerCase().includes("fanduel.com") && window.gtag) {
          window.gtag("event", "customer_login", {
            account_id: customer?.Product_Account_ID,
          });
        }

        // No client-side log_login call — server logs on fetch_customer
        // If you re-enable the video modal, keep this block
        /*
        MicroModal.show('video-modal', {
          awaitCloseAnimation: true,
          onShow: () => videoRef.current?.play?.(),
          onClose: () => videoRef.current?.pause?.(),
        });
        */
      } catch (err) {
        console.log("Customer not found or error occurred", err);
        window.alert(
          "The email address entered is not associated with a FanDuel account or you're not eligible for this program."
        );
        setLoginDisabled(false);
      }
    },
    [email]
  );

  const handleItemSelect = useCallback(
    (name, shields, isSelected, deselectHandler) => {
      const s = parseInt(shields, 10) || 0;

      if (!isSelected) {
        const newTotal = (parseInt(totalShields, 10) || 0) - s;
        if (newTotal >= 0) {
          setTotalShields(newTotal);
          setShieldsUsed((prev) => (parseInt(prev, 10) || 0) + s);
          setNewSelectedPerks((prev) => [...prev, name]);
        } else {
          window.alert("You have exceeded the total number of shields available.");
          deselectHandler?.();
        }
      } else {
        setTotalShields((prev) => (parseInt(prev, 10) || 0) + s);
        setShieldsUsed((prev) => (parseInt(prev, 10) || 0) - s);
        setNewSelectedPerks((prev) => prev.filter((p) => p !== name));
      }
    },
    [totalShields]
  );

  const optionSelectHandler = useCallback((name, option) => {
    setNewSelectedPerks((prev) => {
      const idx = prev.indexOf(name);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = `${name} - ${option}`;
      return next;
    });
  }, []);

  const handleGiftCheckbox = useCallback((e) => {
    setGiftSelected(e.target.checked ? "Yes" : "No");
  }, []);

  const handleCustomExperience = useCallback((e) => {
    setCustomExperience(e.target.value || "");
  }, []);

  const handleHolidayHelper = useCallback((e) => {
    setHolidayHelper(e.target.value || "");
  }, []);

  const handleFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const hasPerks = newSelectedPerks.length > 0;
      const hasGift = giftSelected === "Yes";
      const hasCustom = (customExperience || "").trim() !== "";

      if (!hasPerks && !hasGift && !hasCustom) {
        window.alert(
          "Please select some perks, select a gift, or choose a custom experience."
        );
        return;
      }

      setSubmitDisabled(true);

      const sanitizedGift = stripHtml(giftSelected);
      const sanitizedCustom = stripHtml(customExperience);
      const sanitizedHoliday = stripHtml(holidayHelper);

      const combinedCustom = [
        sanitizedHoliday && `Holiday Helper: ${sanitizedHoliday}`,
        sanitizedCustom && `Experience Of Your Choice: ${sanitizedCustom}`,
      ]
        .filter(Boolean)
        .join(" | ");

      const form = {
        Product_Account_ID: customerData.Product_Account_ID,
        Key_Account_Manager: customerData.Key_Account_Manager,
        First_Name: customerData.First_Name,
        Last_Name: customerData.Last_Name,
        Shields: customerData.Shields,
        Email: email,
        Perks: newSelectedPerks,
        Shields_Used: shieldsUsed,
        Gift_Selected: sanitizedGift,
        Custom_Experience: combinedCustom,
      };

      try {
        // Only submit_selection; server handles audit logging internally
        await submitCustomerForm(form);

        setSubmitted(true);
      } catch (err) {
        console.error("Submission failed:", err);
        window.alert("There was an error. Please try again.");
        setSubmitted(false);
        setSubmitDisabled(false);
      }
    },
    [
      newSelectedPerks,
      giftSelected,
      customExperience,
      shieldsUsed,
      customerData,
      email,
    ]
  );

  // Render helpers
  const SelectionsList = () => {
    const hasCustom = (customExperience || "").trim() !== "";
    const items = [
      ...newSelectedPerks.map((p) => ({ label: p })),
      ...(hasCustom ? [{ label: "Experience of your choice" }] : []),
    ];

    if (items.length === 0) return null;

    return (
      <ul className="feedback__list">
        {items.map((it, idx) => (
          <li key={`${it.label}-${idx}`}>
            {String(it.label).replace(/<br\s*\/?>/gi, " ")}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header__inner">
          <img
            className="header__logo"
            src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/shield-select-logo.png"
            alt="Shield Select"
          />
        </div>
      </header>

      {!isPostLaunch && !isLoggedIn && (
        <ul className="tabs" data-tabs>
          <li className={`tabs-title ${!isSignIn ? "is-active" : ""}`}>
            <a
              href="#"
              aria-selected={!isSignIn}
              onClick={(e) => {
                e.preventDefault();
                setIsSignIn(false);
              }}
            >
              App Launch
            </a>
          </li>
          <li className={`tabs-title ${isSignIn ? "is-active" : ""}`}>
            <a
              href="#"
              aria-selected={isSignIn}
              onClick={(e) => {
                e.preventDefault();
                setIsSignIn(true);
              }}
            >
              Sign In
            </a>
          </li>
        </ul>
      )}
  
      {!isPostLaunch && !isLoggedIn && !isSignIn && (
        <section className="section intro">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
                <h2 className="intro__h2">The new Shield Select Vault is coming soon</h2>
                <p className="intro__p">
                  Access your rewards faster and easier in the FanDuel app.
                </p>
                <p className="intro__p">
                  Beginning January 6, 2026, you’ll be able to access The Shield Select Vault directly in the FanDuel Sportsbook app.<br/><br/>Access rewards faster, easier, and right where you already play.
                </p>

                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />

                <h2 className="intro__h2">Your Loyalty Experience, Elevated</h2>
                <p className="intro__p">
                  Enjoy faster access, richer rewards, and a more seamless Shield Select experience — all in one place.
                </p>
                <ul className="intro__list intro__list--nolist">
                  <li>
                    <h3>Instant access to The Vault - just tap and go.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_InstantAccess.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Explore exclusive rewards and experiences designed for VIPs like you.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_ExploreExclusives_NEW.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Redeem faster with a simplified flow that gets you what you want, quicker.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_RedeemFaster.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Track your activity in real time with simplified status and reward views.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_TrackActivity_NEW.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Request one-of-a-kind rewards through the “Bespoke Request” feature — built around you.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_Bespoke.gif"
                      alt=""
                    />
                  </li>
                </ul>
                <p className="intro__p">
                  The best of Shield Select. Now built right into your app.
                </p>
                
                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />

                <h2 className="faq__h2">Frequently Asked Questions</h2>

                <h3 className="faq__h3">Can I still access the old Vault?</h3>
                <p className="faq__p">
                  Access to the old Vault will be retired as of January 6, 2026.
                </p>

                <h3 className="faq__h3">Will my current Shields automatically transfer over?</h3>
                <p className="faq__p">
                  Yes, you won’t lose a thing. All Shields will automatically be added to The Vault.
                </p>

                <h3 className="faq__h3">What if I have additional questions?</h3>
                <p className="faq__p">
                  Your Account Manager is standing by and ready to help.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {isPostLaunch && (
        <section className="section intro">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
                <h2 className="intro__h2">Shield Select Is Now in the App</h2>
                <p className="intro__p">
                  The Vault. One Tap Away.
                </p>
                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />
                <p className="intro__p">
                  The Shield Select Vault now lives right inside the FanDuel Sportsbook app.<br/><br/>Access rewards faster, easier, and right where you already play.<br/><br/>Welcome to a new era of Shield Select.
                </p>
                <a className="intro__cta" href="#" target="_blank" rel="noopener noreferrer">Access Now</a>

                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />

                <h2 className="intro__h2">Your Loyalty Experience, Elevated</h2>
                <p className="intro__p">
                  Enjoy faster access, richer rewards, and a more seamless Shield Select experience — <span className="nowrap">all in one place.</span>
                </p>
                <ul className="intro__list intro__list--nolist">
                  <li>
                    <h3>Instant access to The Vault - just tap and go.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_InstantAccess.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Explore exclusive rewards and experiences designed for VIPs like you.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_ExploreExclusives_NEW.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Redeem faster with a simplified flow that gets you what you want, quicker.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_RedeemFaster.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Track your activity in real time with simplified status and reward views.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_TrackActivity_NEW.gif"
                      alt=""
                    />
                  </li>
                  <li>
                    <h3>Request one-of-a-kind rewards through the “Bespoke Request” feature — built around you.</h3>
                    <img
                      className="lazyload"
                      data-src="https://s3.us-east-1.amazonaws.com/cdn.fanduel.com/shield-select/images/VIP_ShieldSelects_Bespoke.gif"
                      alt=""
                    />
                  </li>
                </ul>
                <p className="intro__p">
                  The best of Shield Select. Now built right into your app.
                </p>
                <a className="intro__cta" href="#" target="_blank" rel="noopener noreferrer">Access Now</a>

                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />

                <h2 className="intro__h2">How to Access The Vault</h2>
                <p className="intro__p">
                  <strong>Getting into The Vault is easy:</strong>
                </p>
                <p className="intro__p">
                  1. Log in to the FanDuel Sportsbook app
                </p>
                <p className="intro__p">
                  2. Navigate to the Shield Select Vault
                </p>
                <p className="intro__p">
                  3. Explore, redeem, enjoy.
                </p>
                <p className="intro__p">
                  The best of Shield Select. Now built right into your app.
                </p>
                <a className="intro__cta" href="#" target="_blank" rel="noopener noreferrer">Access Now</a>
                
                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />

                <h2 className="faq__h2">Frequently Asked Questions</h2>

                <h3 className="faq__h3">Can I still access the old Vault?</h3>
                <p className="faq__p">
                  Access to the old Vault will be retired as of January 6, 2026.
                </p>

                <h3 className="faq__h3">Will my current Shields automatically transfer over?</h3>
                <p className="faq__p">
                  Yes, you won’t lose a thing. All Shields will automatically be added to The Vault.
                </p>

                <h3 className="faq__h3">What if I have additional questions?</h3>
                <p className="faq__p">
                  Your Account Manager is standing by and ready to help.
                </p>
                <a className="faq__cta" href="#" target="_blank" rel="noopener noreferrer">Access Now</a>
              </div>
            </div>
          </div>
        </section>
      )}

      {!isPostLaunch && isSignIn && (
        <section className="section intro">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
                <h2 className="intro__h2">Unlock Exclusive Experiences & Gifts</h2>
                <img
                  className="intro__bar"
                  src="https://s3.amazonaws.com/cdn.fanduel.com/images/2024/vip-rewards/perks-bar.png"
                  alt=""
                />
                <div className="intro__copy" style={{ display: submitted ? "none" : "block" }}>
                  <p className="intro__p">
                    As a Shield Select member, you have special access to the Shield Select Vault, where you can redeem Shields for exclusive rewards, exceptional experiences, and luxury gifts.
                  </p>
                  <ul className="intro__list">
                    <li>
                      <strong>Wondering when and how Shields can be earned?</strong> Shields will now be <strong>distributed every month</strong> (typically the first Tuesday of every month) and <strong>Shields are based on activity on FanDuel Sportsbook</strong> the prior month and are only available to Shield Select members
                    </li>
                    <li>
                      <strong>Unused Shields will carry over to future months</strong>, so you can save up for bigger rewards or spend as you go.
                    </li>
                    <li>The vault is refreshed monthly with new offerings. If a piece from a previous drop has caught your eye but is no longer listed, please don’t hesitate to get in touch via the “Experience of Your Choice’ option — we’ll do our best to assist</li>
                  </ul>
                  <p className="intro__p">
                    Apply your Shields to as many gifts & experiences as your balance allows. Once you’ve made your request, the Shields will be deducted from your account.
                  </p>
                </div>
              </div>

              {!isLoggedIn && (
                <div className="small-12 small-offset-0 medium-8 medium-offset-2 large-6 large-offset-3 cell intro__form-container">
                  <p className="intro__p">
                    <em>Please use the email address associated with your FanDuel account.</em>
                  </p>
                  <form className="intro__form" onSubmit={handleLogIn} noValidate>
                    <input
                      type="email"
                      className="intro__form-input"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                      placeholder="Enter Email Address"
                      required
                      aria-invalid={errorText ? "true" : "false"}
                    />
                    <button type="submit" className="intro__submit" id="enterBtn" disabled={loginDisabled}>
                      SUBMIT
                    </button>
                  </form>
                  {errorText && <p className="intro__error" role="alert">{errorText}</p>}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {isLoggedIn && !submitted && (
        <>
          <section className="shields">
            <div className="grid-container">
              <div className="grid-x grid-padding-x">
                <div className="small-12 cell">
                  <h3 className="shields__h3">
                    <span>YOUR BALANCE</span>
                    <br />
                    {totalShields}
                  </h3>
                  <p className="shields__p">SHIELDS</p>
                </div>
              </div>
            </div>
          </section>

          <section className="section perks">
            <div className="grid-container">
              <div className="grid-x grid-padding-x">
                <Perks
                  isLoggedIn={isLoggedIn}
                  perksMenu={perksMenu}
                  itemSelectHandler={handleItemSelect}
                  optionSelectHandler={optionSelectHandler}
                  handleFormSubmit={handleFormSubmit}
                  submitDisabled={submitDisabled}
                  customExperienceHandler={handleCustomExperience}
                  holidayHelperHandler={handleHolidayHelper}
                  giftCheckboxHandler={handleGiftCheckbox}
                />
              </div>
            </div>
          </section>
        </>
      )}

      {isLoggedIn && submitted && (
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="small-12 small-offset-0 medium-10 medium-offset-1 large-8 large-offset-2 cell">
              <p className="feedback__p">Your selections have been submitted. A FanDuel employee will reach out with next steps.</p>
              <div className="feedback__selections">
                <SelectionsList />
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="grid-container">
          <div className="grid-x grid-padding-x">
            <div className="small-12 cell">
              <p className="footer__p">
                Participating in the Shield Select program is governed by our{" "}
                <a href="https://www.fanduel.com/vipprogram/terms" target="_blank" rel="noreferrer">
                  VIP Terms and Conditions
                </a>
                <br />
                Gambling Problem? Call 1-800-GAMBLER.
                <br />
                <br />© Betfair Interactive US LLC, 2025
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* If you use the video modal, keep the ref */}
      <video ref={videoRef} style={{ display: "none" }} />
    </div>
  );
}

export default App;
